<template>
  <bm-dialog
    :visible="value"
    :label="label"
    @accept="accept(addressObj)"
    @cancel="cancel"
  >
    <bm-alert v-if="addressLookupFailed" type="warning">
      {{ $m.smart_address_failed_to_resolve_banner }}
    </bm-alert>
    <bm-address-lookup
      :t="$m.keys.smart_address_search"
      @address-retrieved="updateAddressFromRetrieve"
      @failed-to-retrieve-address="failedToRetrieveAddress"
      @retrieving-address="updateRetrievingAddress"
    />
    <div v-for="control in controlList" :key="control.part">
      <component
        :is="control.component"
        v-bind="control.props"
        :disabled="retrievingAddress"
        :loading="retrievingAddress"
        v-on="control.on"
      />
    </div>
  </bm-dialog>
</template>

<script lang="ts">
import { useReactiveDialogSetup } from "@/components/mixins/dialogs";
import type { PropType } from "vue";
import { defineComponent, ref, computed } from "vue";
import messages, { useI18n } from "@/plugins/i18n";
import BmDialog from "@/components/dialogs/BmDialog.vue";
import type { Address } from "@/components/controls/address/addresses";
import {
  AddressParts,
  getAddressPartDisplayOrder,
} from "@/components/controls/address/addresses";
import cloneDeep from "lodash-es/cloneDeep";
import BmText from "@/components/controls/input/BmText.vue";
import BmCountry from "@/components/controls/input/specialised/BmCountry.vue";
import { isNotNullOrUndefined } from "@/app/apiutils";
import BmAlert from "@/components/controls/BmAlert.vue";
import BmAddressLookup from "@/components/controls/address/BmAddressLookup.vue";

export default defineComponent({
  name: "BmAddressCaptureDialog",
  components: { BmDialog, BmAlert, BmAddressLookup },
  props: {
    label: {
      type: String,
      default: messages.address_capture,
    },
    value: {
      type: Boolean,
      required: true,
    },
    address: {
      type: Object as PropType<Address>,
      required: true,
    },
    failedToRetrieve: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, context) {
    const { accept, cancel } = useReactiveDialogSetup(
      ref(props.value),
      context
    );
    const { $m } = useI18n();
    const addressObj = ref<Address>(cloneDeep(props.address));
    const addressLookupFailed = ref(props.failedToRetrieve);
    const retrievingAddress = ref(false);
    const controlList = computed(() =>
      getAddressPartDisplayOrder()
        .map((part) => {
          if (
            addressObj.value.addressBusinessName !== undefined &&
            part === AddressParts.AddressBusinessName
          ) {
            return {
              part,
              component: BmText,
              props: {
                value: addressObj.value.addressBusinessName,
                t: $m.keys.address_business,
                autofocus: true, // Should focus whoever comes first
              },
              on: {
                input: (val: string) =>
                  (addressObj.value.addressBusinessName = val),
              },
            };
          }

          if (part === AddressParts.AddressLine1) {
            return {
              part,
              component: BmText,
              props: {
                value: addressObj.value.addressLine1,
                t: $m.keys.address_line_1,
                autofocus: true, // Should focus whoever comes first
              },
              on: {
                input: (val: string) => (addressObj.value.addressLine1 = val),
              },
            };
          }

          if (part === AddressParts.AddressLine2) {
            return {
              part,
              component: BmText,
              props: {
                value: addressObj.value.addressLine2,
                t: $m.keys.address_line_2,
              },
              on: {
                input: (val: string) => (addressObj.value.addressLine2 = val),
              },
            };
          }

          if (part === AddressParts.AddressLine3) {
            return {
              part,
              component: BmText,
              props: {
                value: addressObj.value.addressLine3,
                t: $m.keys.address_line_3,
              },
              on: {
                input: (val: string) => (addressObj.value.addressLine3 = val),
              },
            };
          }

          if (part === AddressParts.AddressLine4) {
            return {
              part,
              component: BmText,
              props: {
                value: addressObj.value.addressLine4,
                t: $m.keys.address_line_4,
              },
              on: {
                input: (val: string) => (addressObj.value.addressLine4 = val),
              },
            };
          }

          if (part === AddressParts.AddressLine5) {
            return {
              part,
              component: BmText,
              props: {
                value: addressObj.value.addressLine5,
                t: $m.keys.address_line_5,
              },
              on: {
                input: (val: string) => (addressObj.value.addressLine5 = val),
              },
            };
          }

          if (part === AddressParts.AddressPostcode) {
            return {
              part,
              component: BmText,
              props: {
                value: addressObj.value.addressPostcode,
                t: $m.keys.address_postcode,
              },
              on: {
                input: (val: string) =>
                  (addressObj.value.addressPostcode = val),
              },
            };
          }

          if (part === AddressParts.AddressLine6) {
            return {
              part,
              component: BmCountry,
              props: {
                value: addressObj.value.addressLine6,
                t: $m.keys.address_line_6,
                clearable: true,
              },
              on: {
                input: (val: string) => (addressObj.value.addressLine6 = val),
              },
            };
          }
        })
        .filter(isNotNullOrUndefined)
    );

    const updateAddressFromRetrieve = (retrievedAddress: Address) => {
      addressObj.value = retrievedAddress;
      retrievingAddress.value = false;
    };

    const failedToRetrieveAddress = () => {
      addressLookupFailed.value = true;
      retrievingAddress.value = false;
    };

    const updateRetrievingAddress = () => {
      retrievingAddress.value = true;
      addressLookupFailed.value = false;
    };

    return {
      accept,
      cancel,
      updateAddressFromRetrieve,
      updateRetrievingAddress,
      failedToRetrieveAddress,
      addressLookupFailed,
      retrievingAddress,
      controlList,
      addressObj,
    };
  },
});
</script>

<style scoped></style>
